import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { ProductListItem } from '../api/types'
import { formatMoney, formatMoment, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, Panel, Spinner, TextInput,
} from '@shop/ui'

type OfferRow = {
  id: string
  productId: string
  name: string
  regularPrice: number
  offerPrice: number
  startsAt: string
  endsAt: string
}

type PlanLine = { variantId: string; name: string; regularPrice: number; offerPrice: number }

/** يومٌ بعد عددٍ من الأيّام، الساعة ١١ مساءً بتوقيت عمّان — نهايةُ يوم العمل. */
function daysFromNow(days: number): string {
  const at = new Date(Date.now() + days * 86_400_000)
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(at)

  return `${day}T23:00`
}

/** «2026-10-05T23:00» بتوقيت عمّان ⇐ ISO. وعمّانُ على +03:00 طوالَ السنة. */
function ammanToIso(local: string): string {
  return `${local}:00+03:00`
}

/**
 * العروض — <b>خصمٌ بسيطٌ على السعر الموجود، لمدّة</b>.
 *
 * <p>
 * ⚠ <b>قرارُ المالك ٢٠٢٦-٠٩-٢٩: «العروض اخصم اشي بسيط عن السعر الموجود».</b>
 * فالافتراضُ ٥٪ لأسبوع، والخصمُ نسبةٌ أو مبلغ. ويُعاين السعرُ الجديدُ لكلّ صنفٍ قبل
 * أن يُعلَن — ويسري في نقطة البيع وعلى الموقع معًا، ويعود السعرُ الأصليّ وحدَه
 * حين ينتهي.
 * </p>
 *
 * <p>
 * ⚠ <b>ولا يُقبل عرضٌ تحت الكلفة وأدنى هامش</b> — الخادمُ يردّه باسم الصنف: عرضٌ
 * يُعلَن على الموقع ثمّ يرفضه الصندوقُ أسوأُ من لا عرض.
 * </p>
 */
export default function Offers() {
  const { can } = useAuth()
  const queries = useQueryClient()

  const [term, setTerm] = useState('')
  const [picked, setPicked] = useState<ProductListItem[]>([])
  const [mode, setMode] = useState<'percent' | 'amount'>('percent')
  const [value, setValue] = useState('5')
  const [endsAt, setEndsAt] = useState(daysFromNow(7))
  const [plan, setPlan] = useState<PlanLine[] | null>(null)

  const live = useQuery({
    queryKey: ['offers'],
    queryFn: () => api<OfferRow[]>('/api/v1/offers'),
  })

  const found = useQuery({
    queryKey: ['offer-search', term.trim()],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=20&q=${encodeURIComponent(term.trim())}`),
    enabled: term.trim().length >= 2,
    placeholderData: keepPreviousData,
  })

  const amount = toNumber(value)

  const body = () => ({
    productIds: picked.map((p) => p.id),
    percent: mode === 'percent' ? amount : null,
    amount: mode === 'amount' ? amount : null,
    endsAt: ammanToIso(endsAt),
  })

  const preview = useMutation({
    mutationFn: () => api<PlanLine[]>('/api/v1/offers/preview', { method: 'POST', body: body() }),
    onSuccess: setPlan,
  })

  const create = useMutation({
    mutationFn: () => api<OfferRow[]>('/api/v1/offers', { method: 'POST', body: body() }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['offers'] })
      setPicked([])
      setPlan(null)
      setTerm('')
    },
  })

  const end = useMutation({
    mutationFn: (id: string) => api(`/api/v1/offers/${id}/end`, { method: 'POST' }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['offers'] }),
  })

  const editable = can(Perm.priceEdit)
  const ready = picked.length > 0 && amount !== null && amount > 0 && endsAt !== ''

  return (
    <>
      {editable && (
        <Panel title="عرضٌ جديد">
          <Banner tone="info">
            العرضُ يسري في نقطة البيع وعلى الموقع معًا، ويعود السعرُ الأصليّ وحدَه حين ينتهي.
          </Banner>

          <Field label="الأصناف" hint="ابحث وأضف صنفًا أو أكثر">
            <TextInput value={term} onChange={(v) => { setTerm(v); setPlan(null) }} placeholder="الاسمُ أو الرمز…" />
          </Field>

          {found.isFetching && <Spinner label="يُبحث…" />}

          {term.trim().length >= 2 && (
            <ul className="mt-1 grid gap-1">
              {(found.data ?? [])
                .filter((p) => !picked.some((x) => x.id === p.id))
                .slice(0, 8)
                .map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-line px-3 py-1.5 text-right text-sm hover:bg-surface-raised"
                      onClick={() => { setPicked([...picked, p]); setPlan(null) }}
                    >
                      + {p.nameAr}
                    </button>
                  </li>
                ))}
            </ul>
          )}

          {picked.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {picked.map((p) => (
                <span key={p.id} className="flex items-center gap-1 rounded-full bg-surface-raised px-3 py-1 text-sm">
                  {p.nameAr}
                  <button
                    type="button"
                    aria-label="إزالة"
                    className="text-ink-soft"
                    onClick={() => { setPicked(picked.filter((x) => x.id !== p.id)); setPlan(null) }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Field label="الخصم">
              <div className="flex gap-2">
                <TextInput value={value} onChange={(v) => { setValue(v); setPlan(null) }} />
                <select
                  className="rounded-lg border border-line bg-surface px-2"
                  value={mode}
                  onChange={(e) => { setMode(e.target.value as 'percent' | 'amount'); setPlan(null) }}
                >
                  <option value="percent">٪</option>
                  <option value="amount">دينار</option>
                </select>
              </div>
            </Field>

            <Field label="ينتهي">
              <input
                type="datetime-local"
                className="w-full rounded-lg border border-line bg-surface px-3 py-2"
                value={endsAt}
                onChange={(e) => { setEndsAt(e.target.value); setPlan(null) }}
              />
            </Field>

            <div className="flex items-end gap-2">
              <Button disabled={!ready || preview.isPending} onClick={() => preview.mutate()}>معاينة</Button>
            </div>
          </div>

          <ErrorBanner error={preview.error ?? create.error} />

          {plan && (
            <div className="mt-3">
              <table className="w-full text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr className="border-b border-line">
                    <th className="p-2 text-right">الصنف</th>
                    <th className="p-2 text-left">السعر</th>
                    <th className="p-2 text-left">بالعرض</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((line) => (
                    <tr key={line.variantId} className="border-b border-line/50">
                      <td className="p-2">{line.name}</td>
                      <td className="tabular p-2 text-left text-ink-soft line-through" dir="ltr">
                        {formatMoney(line.regularPrice)}
                      </td>
                      <td className="tabular p-2 text-left font-bold" dir="ltr">{formatMoney(line.offerPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-3">
                <Button tone="primary" disabled={create.isPending} onClick={() => create.mutate()}>
                  أعلن العرض
                </Button>
              </div>
            </div>
          )}
        </Panel>
      )}

      <Panel title="العروضُ السارية">
        <ErrorBanner error={live.error ?? end.error} />
        {live.isPending && <Spinner />}
        {live.data && live.data.length === 0 && <Empty>لا عرضَ ساريًا الآن.</Empty>}

        <ul className="grid gap-2">
          {live.data?.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2">
              <span className="min-w-0 flex-1 font-semibold">{o.name}</span>
              <span className="tabular text-sm text-ink-soft line-through" dir="ltr">{formatMoney(o.regularPrice)}</span>
              <b className="tabular" dir="ltr">{formatMoney(o.offerPrice)}</b>
              <span className="text-xs text-ink-soft">حتّى {formatMoment(o.endsAt)}</span>
              {editable && (
                <Button tone="danger" disabled={end.isPending} onClick={() => end.mutate(o.id)}>أنهِه الآن</Button>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </>
  )
}
